import { useMemo } from 'react';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { usePermissions } from './usePermissions';

/**
 * May the signed-in person change the workspace's attributes — add a scoring
 * dimension, rename one, change its scales and their anchors? One answer for
 * Settings → Attributes, Custom fields and the attribute page, so none of them
 * draws a control the person may not use (build contract §2.8). Was written out
 * twice, in AttributeManagement and CustomFields; the attribute page asked
 * nothing (roadmap 1C V-10).
 *
 * attributes.define or criteria.create in the current workspace, or the legacy
 * canManageAttributes (criteria.edit, or an admin role).
 */
export function useCanDefineAttributes(): boolean {
  const permissions = usePermissions();
  const can = useUserStore((state) => state.can);
  const currentWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);
  const settingsScope = useMemo(
    () =>
      currentWorkspaceId
        ? { kind: 'resource' as const, id: currentWorkspaceId }
        : { kind: 'organization' as const, id: '*' },
    [currentWorkspaceId]
  );
  return (
    permissions.canManageAttributes ||
    can('attributes.define', settingsScope) ||
    can('criteria.create', settingsScope)
  );
}

/** The line shown in place of an edit control the person may not use. */
export const DEFINE_ATTRIBUTES_NEEDS =
  'Changing attributes needs the right to define attributes or create criteria — a workspace admin or the Criteria Authority holds it.';
